import { useParams, useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  ArrowLeft, FolderKanban, Users, ListChecks, Bug, BarChart3, LayoutGrid, List, Search,
  Calendar, RotateCcw, ShieldCheck
} from "lucide-react";
import KanbanBoard from "@/components/KanbanBoard";
import BugList from "@/components/BugList";
import ProjectReport from "@/components/ProjectReport";
import {
  TASK_STATUSES, TASK_STATUS_LABELS, TASK_PRIORITIES, statusBadgeClass,
  roleBadgeClass, ROLE_LABELS, priorityBadgeClass
} from "@/lib/constants";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import Pagination from "@/components/Pagination";
import usePaginatedList from "@/hooks/usePaginatedList";
import { useAuth } from "@/contexts/AuthContext";

export default function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user: me } = useAuth();

  const searchParams = new URLSearchParams(location.search);
  const paramTab = searchParams.get("tab");
  const taskId = searchParams.get("taskId");
  const bugId = searchParams.get("bugId");

  const [project, setProject] = useState(null);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [viewMode, setViewMode] = useState("board");

  // Task filters
  const [taskQ, setTaskQ] = useState("");
  const [taskStatusFilter, setTaskStatusFilter] = useState("all");
  const [taskPriorityFilter, setTaskPriorityFilter] = useState("all");
  const [taskAssigneeFilter, setTaskAssigneeFilter] = useState("all");
  const [taskFromDate, setTaskFromDate] = useState("");
  const [taskToDate, setTaskToDate] = useState("");
  const [myTasksOnly, setMyTasksOnly] = useState(false);

  const paginatedTasks = usePaginatedList("/tasks", {
    project_id: id,
    q: taskQ,
    status: taskStatusFilter === "all" ? "" : taskStatusFilter,
    priority: taskPriorityFilter === "all" ? "" : taskPriorityFilter,
    assignee_id: myTasksOnly ? me?.id : (taskAssigneeFilter === "all" ? "" : taskAssigneeFilter),
    from_date: taskFromDate,
    to_date: taskToDate,
  });

  useEffect(() => {
    const handleTimerStopped = () => {
      paginatedTasks.refresh();
    };
    window.addEventListener("timer-stopped", handleTimerStopped);
    return () => {
      window.removeEventListener("timer-stopped", handleTimerStopped);
    };
  }, [paginatedTasks]);

  useEffect(() => {
    if (taskId) {
      setActiveTab("tasks");
      setViewMode("board");
    } else if (bugId) {
      setActiveTab("bugs");
    } else if (paramTab) {
      setActiveTab(paramTab);
    }
  }, [taskId, bugId, paramTab]);

  const handleCloseTaskDetail = () => {
    const params = new URLSearchParams(location.search);
    if (params.has("taskId")) {
      params.delete("taskId");
      const qs = params.toString();
      navigate(`${location.pathname}${qs ? `?${qs}` : ""}`, { replace: true });
    }
  };

  const handleCloseBugDetail = () => {
    const params = new URLSearchParams(location.search);
    if (params.has("bugId")) {
      params.delete("bugId");
      const qs = params.toString();
      navigate(`${location.pathname}${qs ? `?${qs}` : ""}`, { replace: true });
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [p, u, t] = await Promise.all([
          api.get(`/projects/${id}`),
          api.get(`/projects/${id}/members`),
          api.get("/teams/all").catch(() => ({ data: [] })),
        ]);
        setProject(p.data);
        setUsers(u.data);
        setTeams(t.data);
      } catch (err) {
        toast.error("Project not found or access denied");
        navigate("/projects");
      }
    };

    load();
  }, [id, navigate]);

  if (!project) return <div className="text-sm text-muted-foreground" data-testid="project-loading">Loading…</div>;

  const userMap = Object.fromEntries(users.map((u) => [u.id, u]));
  const teamMap = Object.fromEntries(teams.map((t) => [t.id, t]));
  const members = (project.member_ids || []).map((mid) => userMap[mid]).filter(Boolean);
  const projectTeams = project.team_details && project.team_details.length > 0
    ? project.team_details
    : (project.team_ids || []).map((tid) => teamMap[tid]).filter(Boolean);

  // Combine project members and team lead for eligible task/bug assignees
  const leadUser = userMap[project.team_lead_id];
  const projectAssignees = leadUser && !members.some((m) => m.id === leadUser.id)
    ? [leadUser, ...members]
    : members;

  // Segregate members according to team
  const teamsWithMembers = projectTeams.map((team) => {
    const fullTeam = teamMap[team.id] || team;
    const teamMemberIds = new Set(fullTeam.member_ids || []);
    if (fullTeam.lead_id) teamMemberIds.add(fullTeam.lead_id);
    const teamUsers = Array.from(teamMemberIds).map((uid) => userMap[uid]).filter(Boolean);
    return {
      ...fullTeam,
      lead: userMap[fullTeam.lead_id],
      members: teamUsers,
    };
  });

  const allTeamMemberIds = new Set(teamsWithMembers.flatMap((t) => t.members.map((m) => m.id)));
  const directMembers = users.filter((u) => !allTeamMemberIds.has(u.id));

  const resetTaskFilters = () => {
    setTaskQ("");
    setTaskStatusFilter("all");
    setTaskPriorityFilter("all");
    setTaskAssigneeFilter("all");
    setTaskFromDate("");
    setTaskToDate("");
    setMyTasksOnly(false);
  };

  return (
    <div className="space-y-6" data-testid="project-detail-page">
      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate("/projects")} className="mb-3" data-testid="back-to-projects">
          <ArrowLeft className="w-4 h-4 mr-1" /> All Projects
        </Button>
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <FolderKanban className="w-5 h-5 text-primary" />
              <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">{project.name}</h1>
            </div>
            <p className="text-sm text-muted-foreground max-w-2xl">{project.description}</p>
          </div>
          <Badge variant="outline" className="uppercase text-xs">{project.status}</Badge>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList data-testid="project-tabs">
          <TabsTrigger value="overview" data-testid="project-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="project-tab-tasks"><ListChecks className="w-3.5 h-3.5 mr-1" /> Tasks</TabsTrigger>
          <TabsTrigger value="bugs" data-testid="project-tab-bugs"><Bug className="w-3.5 h-3.5 mr-1" /> Bugs</TabsTrigger>
          <TabsTrigger value="members" data-testid="project-tab-members"><Users className="w-3.5 h-3.5 mr-1" /> Members</TabsTrigger>
          <TabsTrigger value="reports" data-testid="project-tab-reports"><BarChart3 className="w-3.5 h-3.5 mr-1" /> Reports</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="p-5">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Team Lead</div>
              <div className="font-heading font-semibold mt-2">{userMap[project.team_lead_id]?.name || "—"}</div>
            </Card>
            <Card className="p-5">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Timeline</div>
              <div className="text-sm mt-2 font-mono">
                {project.start_date || "—"} → {project.end_date || "—"}
              </div>
            </Card>
            <Card className="p-5">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Teams</div>
              <div className="flex flex-wrap gap-1 mt-2">
                {projectTeams.map((t) => <Badge key={t.id} variant="outline">{t.name}</Badge>)}
                {projectTeams.length === 0 && <span className="text-sm text-muted-foreground">No teams</span>}
              </div>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="tasks" className="space-y-4">
          <div className="space-y-3 border-b pb-3">
            <div className="flex flex-wrap gap-3 items-center justify-between">
              <div className="flex flex-wrap gap-2.5 items-center flex-1">
                <div className="relative min-w-[200px] flex-1">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={taskQ}
                    onChange={(e) => setTaskQ(e.target.value)}
                    placeholder="Search tasks by title..."
                    className="pl-9 text-xs h-9"
                    data-testid="project-tasks-search"
                  />
                </div>
                <label className="flex items-center gap-2 text-xs font-medium select-none cursor-pointer border rounded-md px-3 h-9 bg-background hover:bg-muted/30">
                  <input
                    type="checkbox"
                    checked={myTasksOnly}
                    onChange={(e) => setMyTasksOnly(e.target.checked)}
                    className="w-3.5 h-3.5 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span>My Tasks</span>
                </label>

                {viewMode === "list" && (
                  <>
                    <Select value={taskStatusFilter} onValueChange={setTaskStatusFilter}>
                      <SelectTrigger className="w-36 h-9 text-xs">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        {TASK_STATUSES.map((s) => (
                          <SelectItem key={s} value={s}>{TASK_STATUS_LABELS[s]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select value={taskPriorityFilter} onValueChange={setTaskPriorityFilter}>
                      <SelectTrigger className="w-36 h-9 text-xs">
                        <SelectValue placeholder="Priority" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All priorities</SelectItem>
                        {TASK_PRIORITIES.map((p) => (
                          <SelectItem key={p} value={p}>{p}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select value={taskAssigneeFilter} onValueChange={setTaskAssigneeFilter}>
                      <SelectTrigger className="w-40 h-9 text-xs">
                        <SelectValue placeholder="Assignee" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All assignees</SelectItem>
                        {users.map((u) => (
                          <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </>
                )}
              </div>

              <div className="flex justify-end gap-2" data-testid="tasks-view-toggle">
                <Button
                  variant={viewMode === "board" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setViewMode("board")}
                  data-testid="view-board-btn"
                >
                  <LayoutGrid className="w-4 h-4 mr-1" /> Board
                </Button>
                <Button
                  variant={viewMode === "list" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setViewMode("list")}
                  data-testid="view-list-btn"
                >
                  <List className="w-4 h-4 mr-1" /> List
                </Button>
              </div>
            </div>

            {viewMode === "list" && (
              <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-border/40 text-xs">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Calendar className="w-3.5 h-3.5" />
                  <span>Created Date:</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">From:</span>
                  <Input
                    type="date"
                    value={taskFromDate}
                    onChange={(e) => setTaskFromDate(e.target.value)}
                    className="w-36 h-8 text-xs font-mono"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">To:</span>
                  <Input
                    type="date"
                    value={taskToDate}
                    onChange={(e) => setTaskToDate(e.target.value)}
                    className="w-36 h-8 text-xs font-mono"
                  />
                </div>
                {(taskQ || taskStatusFilter !== "all" || taskPriorityFilter !== "all" || taskAssigneeFilter !== "all" || taskFromDate || taskToDate || myTasksOnly) && (
                  <Button variant="ghost" size="sm" onClick={resetTaskFilters} className="h-8 text-xs text-muted-foreground hover:text-foreground">
                    <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset
                  </Button>
                )}
              </div>
            )}
          </div>

          {viewMode === "board" ? (
            <KanbanBoard projectId={project.id} users={projectAssignees} teams={teams} defaultTaskId={taskId} q={taskQ} myTasksOnly={myTasksOnly} onCloseDetail={handleCloseTaskDetail} />
          ) : (
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr className="text-left">
                      <th className="px-4 py-3 font-semibold">S.No</th>
                      <th className="px-4 py-3 font-semibold">Title</th>
                      <th className="px-4 py-3 font-semibold">Assignee & Tagged</th>
                      <th className="px-4 py-3 font-semibold">Priority</th>
                      <th className="px-4 py-3 font-semibold">Status</th>
                      <th className="px-4 py-3 font-semibold">Est (h)</th>
                      <th className="px-4 py-3 font-semibold">Deadline Δ</th>
                      <th className="px-4 py-3 font-semibold">Due</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedTasks.items.map((t, i) => (
                      <tr
                        key={t.id}
                        className="border-t border-border hover:bg-muted/30 cursor-pointer"
                        data-testid={`project-task-row-${t.id}`}
                        onClick={() => {
                          setViewMode("board");
                          navigate(`/projects/${project.id}?tab=tasks&taskId=${t.id}`);
                        }}
                      >
                        <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                          {(paginatedTasks.page - 1) * paginatedTasks.pageSize + i + 1}
                        </td>
                        <td className="px-4 py-3 font-medium" title={t.title}>
                          {t.title.length > 30 ? t.title.slice(0, 30) + "..." : t.title}
                          {t.parent_title && (
                            <div className="text-[10px] text-muted-foreground">
                              Subtask of: {t.parent_title}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs">
                          <div className="font-medium text-foreground">
                            {userMap[t.assignee_id]?.name || "Unassigned"}
                          </div>
                          {t.tagged_user_ids && t.tagged_user_ids.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {t.tagged_user_ids.map((uid) => (
                                <Badge key={uid} variant="secondary" className="text-[10px] py-0 px-1 font-normal">
                                  @{userMap[uid]?.name?.split(" ")[0] || "User"}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className={priorityBadgeClass(t.priority)}>{t.priority}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className={statusBadgeClass(t.status)}>{TASK_STATUS_LABELS[t.status]}</Badge>
                        </td>
                        <td className="px-4 py-3 font-mono">{t.estimated_hours ?? "—"}</td>
                        <td className="px-4 py-3 font-mono">{t.deadline_changes || 0}</td>
                        <td className="px-4 py-3 text-xs font-mono text-muted-foreground">{t.due_date || "—"}</td>
                      </tr>
                    ))}
                    {paginatedTasks.items.length === 0 && (
                      <tr>
                        <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                          No tasks match the filter
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination
                page={paginatedTasks.page}
                pageSize={paginatedTasks.pageSize}
                total={paginatedTasks.total}
                onPageChange={paginatedTasks.setPage}
                onPageSizeChange={paginatedTasks.setPageSize}
                testidPrefix="project-tasks-pagination"
              />
            </Card>
          )}
        </TabsContent>

        <TabsContent value="bugs">
          <BugList projectId={project.id} users={projectAssignees} defaultBugId={bugId} onCloseDetail={handleCloseBugDetail} />
        </TabsContent>

        {/* Segregated Members Tab */}
        <TabsContent value="members" className="space-y-6">
          {/* Project Team Lead */}
          {leadUser && (
            <div className="space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-primary" />
                Project Team Lead
              </div>
              <Card className="p-4 border-primary/30 bg-primary/5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm">
                    {leadUser.name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold">{leadUser.name}</div>
                    <div className="text-xs text-muted-foreground">{leadUser.email}</div>
                  </div>
                  <Badge variant="default" className="text-xs">Project Lead</Badge>
                </div>
              </Card>
            </div>
          )}

          {/* Teams and their segregated members */}
          {teamsWithMembers.map((t) => (
            <div key={t.id} className="space-y-3" data-testid={`team-group-${t.id}`}>
              <div className="flex items-center justify-between border-b pb-2">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-primary" />
                  <h3 className="font-semibold text-base">{t.name}</h3>
                  <Badge variant="secondary" className="font-mono text-xs">
                    {t.members.length} members
                  </Badge>
                </div>
                {t.lead && (
                  <span className="text-xs text-muted-foreground">
                    Lead: <span className="font-medium text-foreground">{t.lead.name}</span>
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {t.members.map((m) => (
                  <Card key={m.id} className="p-3.5 flex items-center gap-3 border hover:border-primary/40 transition-colors" data-testid={`project-member-${m.id}`}>
                    <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-xs">
                      {m.name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{m.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{m.email}</div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge variant="outline" className={roleBadgeClass(m.role)}>{ROLE_LABELS[m.role]}</Badge>
                      {t.lead_id === m.id && (
                        <span className="text-[10px] text-primary font-medium">Team Lead</span>
                      )}
                    </div>
                  </Card>
                ))}
                {t.members.length === 0 && (
                  <div className="col-span-full text-xs text-muted-foreground italic py-2">
                    No members assigned to this team yet.
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Direct Project Members (if any members are not in teams) */}
          {directMembers.length > 0 && (
            <div className="space-y-3" data-testid="direct-members-group">
              <div className="flex items-center justify-between border-b pb-2">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-muted-foreground" />
                  <h3 className="font-semibold text-base">Direct Members</h3>
                  <Badge variant="outline" className="font-mono text-xs">
                    {directMembers.length}
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {directMembers.map((m) => (
                  <Card key={m.id} className="p-3.5 flex items-center gap-3 border" data-testid={`project-member-${m.id}`}>
                    <div className="w-9 h-9 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center font-semibold text-xs">
                      {m.name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{m.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{m.email}</div>
                    </div>
                    <Badge variant="outline" className={roleBadgeClass(m.role)}>{ROLE_LABELS[m.role]}</Badge>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {teamsWithMembers.length === 0 && directMembers.length === 0 && (
            <Card className="p-8 text-center text-sm text-muted-foreground">
              No members assigned to this project yet.
            </Card>
          )}
        </TabsContent>

        <TabsContent value="reports">
          <ProjectReport projectId={project.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
